import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactNode } from "react";
import ErrorPage from "@app/ErrorPage";

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (e: Error) => void;
}

interface State {
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Send error to Sentry with full component stack context
    Sentry.withScope((scope) => {
      // Tag this as a React error boundary error
      scope.setTag("error.boundary", "react");
      scope.setTag("error.type", "render");

      // Add component stack as extra context for debugging
      if (errorInfo.componentStack) {
        scope.setExtra("componentStack", errorInfo.componentStack);
      }

      // Add error name for better categorization
      scope.setExtra("errorName", error.name);

      // Capture the original error (not wrapped) for accurate stack traces
      Sentry.captureException(error);
    });

    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  public render() {
    if (this.state?.error) {
      return <ErrorPage />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
